import { useEffect, useRef, useState } from 'react'
import styles from './chat.module.css'

import { io } from 'socket.io-client'
import { swalConfirm, swalErrorAlert } from '../../utils/swal/swal'
import { useSocketStore } from '../../store/socketStore'

export default function ChatRoom({ roomId, setRoomId, senderInfo, receiverInfo }) {
  const userId = senderInfo?.user_id

  const {
    socket,
    initSocket,

    joinRoom,
    leaveRoom,

    getPreviousMessages,
    onPreviousMessages,
    offPreviousMessages,

    onLoginStatus,

    onReadMessage,
    offReadMessage,
    sendMessage,
    onReceiveMessage,
    offReceiveMessage,

    deleteMessage,
    onDeleteMessage,
    offDeleteMessage,

    onError,
    offError,
  } = useSocketStore()

  const [messages, setMessages] = useState([]) // 채팅 리스트
  const [otherLoginStatus, setOtherLoginStatus] = useState(false) // 상대방 채팅방 접속 상태

  useEffect(() => {
    if (!roomId) return

    if (!socket) {
      initSocket(userId)
    }

    // 1. 채팅방 입장
    joinRoom(roomId, senderInfo)

    // 2-1. 이전 대화 목록 요청
    getPreviousMessages(roomId)

    // 2-2. 이전 대화 불러와서 state에 저장
    onPreviousMessages((prevMsgs) => {
      setMessages(prevMsgs)
    })

    // 3. 상대방 채팅방 접속 상태 업데이트
    onLoginStatus((userData) => {
      const exist = userData.some((user) => user.user_id === receiverInfo.user_id)
      setOtherLoginStatus(exist)
    })

    // 4. 이전 채팅 읽음 처리
    onReadMessage(() => {
      setMessages((prevMsg) => {
        const result = prevMsg.map((msg) => {
          if (msg.sender_id === userId && msg.is_read === 0) {
            return { ...msg, is_read: 1 }
          }
          return msg
        })
        return result
      })
    })

    // 5. 채팅 수신
    onReceiveMessage((msg) => {
      setMessages((prev) => [...prev, msg])
    })

    // 6. 채팅 삭제 처리
    onDeleteMessage((messageId) => {
      setMessages((prev) => {
        const newMsg = prev.map((msg) => {
          if (msg.message_id === messageId) {
            msg.is_delete = 1
          }
          return msg
        })
        return newMsg
      })
    })

    // 6. 에러 처리
    onError((error) => {
      console.log(error)
      swalErrorAlert('채팅 에러', error.message)
    })

    // 채팅방 컴포넌트언마운트
    return () => {
      if (socket) {
        leaveRoom(roomId)
        offPreviousMessages()
        offReadMessage()
        offReceiveMessage()
        offDeleteMessage()
        offError()
      }
    }
  }, [roomId])

  // 채팅 삭제
  const handleDeleteMessage = (messageId) => {
    if (!messageId) return
    swalConfirm(
      '채팅 삭제',
      '해당 채팅을 삭제하시겠습니까?',
      'question',
      '삭제',
      '취소',
      () => {
        deleteMessage({ roomId, messageId })
      },
      () => {
        return
      }
    )
  }

  /* Scroll */
  const scrollRef = useRef(null)
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  return (
    <div className={styles.chatRoom}>
      <div className={styles.chatHeader}>
        <div className={styles.otherName}>
          {receiverInfo.user_name}
          <span className={`${styles.otherStatus} ${otherLoginStatus ? styles.active : ''}`} />
        </div>
        <button type="button" className="btn" onClick={() => setRoomId('')}>
          대화나가기
        </button>
      </div>
      <div className={styles.messageList} ref={scrollRef}>
        {messages.map((msg, index) => {
          return (
            <div
              key={index}
              className={`${styles.messageItem}
                ${msg.sender_id === senderInfo.user_id ? styles.myMessage : styles.otherMessage} `}>
              {msg.is_delete ? (
                // 삭제된 메세지
                <div className={styles.deleteMessage}>삭제된 메세지입니다.</div>
              ) : (
                // 메세지
                <div>{msg.message}</div>
              )}

              {/* 읽음여부 */}
              {msg.sender_id === senderInfo.user_id && msg.is_read === 0 && (
                <span className={styles.messageRead}>1</span>
              )}

              {/* 날짜 */}
              <span className={styles.messageDate}>{msg.date}</span>

              {/* 삭제버튼 */}
              {msg.sender_id === senderInfo.user_id && msg.is_delete === 0 && (
                <span
                  className={`${styles.deleteMessageBtn} text-danger`}
                  onClick={() => handleDeleteMessage(msg.message_id)}>
                  X
                </span>
              )}
            </div>
          )
        })}
      </div>
      <ChatInput
        sendMessage={sendMessage}
        roomId={roomId}
        senderId={senderInfo?.user_id}
        receiverId={receiverInfo.user_id}
      />
    </div>
  )
}

function ChatInput({ sendMessage, roomId, senderId, receiverId }) {
  // 채팅 보내기
  const handleSend = () => {
    const input = document.getElementById('chat-input')
    const message = input?.value.trim()
    if (!roomId || !senderId || !receiverId || !message) return

    sendMessage({ roomId, senderId, receiverId, message })
    input.value = ''
  }

  return (
    <div className={styles.chatInputArea}>
      <textarea
        id="chat-input"
        className={styles.chatInput}
        type="text"
        placeholder="메시지를 입력하세요..."
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
          }
        }}
      />
      <button className={styles.sendButton} onClick={handleSend}>
        보내기
      </button>
    </div>
  )
}
